"use client";
import Button from "@/components/buttons";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import cn from "@/utils/cn";
import UserProfileSidebar from "./UserProfileSidebar";
// Theme toggle is hidden for now — not part of the current dashboard design.
// Re-enable by uncommenting this import and the <ThemeToggle /> usages below.
// import ThemeToggle from "@/components/ThemeToggle";
import { useEffect, useState } from "react";
import { useZStore } from "@/utils/z-store";
import { useWorkspacePermissions } from "@/app/(dashboard)/dashboard/_hooks/useWorkspacePermissions";
import { isBasicPlan } from "@/shared/workspace-permissions";
import DashboardDropdown from "./DashboardDropdown";
import CustomReportDropdown from "./CustomReportDropdown";
import FormSelectorDropdown from "./FormSelectorDropdown";
import { useSession } from "next-auth/react";
import axios from "axios";
import { NewEditIcon } from "../icons";
import TextComponent from "@/app/_components/TextComponent";
import { ExportReportIcon } from "@/assets/svg-icons/svg-icons";
import { FiUserPlus } from "react-icons/fi";
import { production } from "@/utils/production-check";
import PremiumUpgradeButton from "@/components/buttons/PremiumUpgradeButton";

// Remove hardcoded disabled - will use banner state from store

// Reusable EditButton component
const EditButton = ({
  href,
  disabled = false,
  className = "",
}: {
  href: string;
  disabled?: boolean;
  className?: string;
}) => {
  return (
    <div className={cn("my-auto", className)}>
      <Link
        href={disabled ? "#" : href}
        className={cn(
          "group flex size-fit items-center rounded-[13px] hover:bg-kd-lte-blue xl:rounded-[17px]",
          disabled
            ? "hover:bg-kd-secondary-4 bg-kd-secondary-4 cursor-not-allowed"
            : "",
        )}
        onClick={disabled ? (e) => e.preventDefault() : undefined}
      >
        <div
          className={cn(
            "relative flex size-9 items-center justify-center rounded-[13px] bg-kd-lte-blue xl:size-12 xl:rounded-[17px]",
            disabled ? "bg-kd-secondary-4" : "",
          )}
        >
          <Image
            alt="Edit icon"
            className={cn(
              "absolute inset-0 m-auto size-[20px] xl:size-[26px]",
              disabled ? "opacity-80" : "",
            )}
            src={NewEditIcon}
            width={100}
            height={100}
          />
        </div>
        <TextComponent
          type="h4"
          className={cn(
            "mr-2 hidden text-white transition-all duration-200 ease-in-out group-hover:inline xl:mr-[11px]",
            disabled ? "text-white" : "",
          )}
        >
          Edit
        </TextComponent>
      </Link>
    </div>
  );
};

const Header = ({ className }: { className?: string }) => {
  const { data, actions } = useZStore();
  const { data: session } = useSession();
  const pathName = usePathname();
  const route = pathName.split("/")[2];
  const isAdminUser = Boolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (session?.user as any)?.isAdmin ||
      session?.user?.email === "yashkad412@gmail.com",
  );

  // Use banner visibility state as disabled state
  // Use fallback to true if isBannerVisible is undefined (during hydration)
  const disabled = data.isBannerVisible ?? true;

  // Workspace-scoped signals for the dashboard header (§WS1/§WS3): premium
  // FEATURES follow the ACTIVE workspace's owner plan, and content edits are a
  // role capability. Read once here (before any early return) so both apply.
  const { can, planType, isFrozen } = useWorkspacePermissions();

  const [profilePicture, setProfilePicture] = useState(
    session?.user?.image || "",
  );

  useEffect(() => {
    const getProfilePicture = async () => {
      try {
        const response = await axios.get("/api/profile-picture");
        setProfilePicture(response.data.profilePicture);
        actions.setProfilePicture(response.data.profilePicture);
      } catch (error) {
        console.error("Error fetching profile picture:", error);
      }
    };
    if (session && session.user.id) getProfilePicture();
  }, [session, actions]);

  if (route.toLocaleLowerCase() === "edit") return <EditDashboardHeader />;
  if (route.toLocaleLowerCase() === "form-builder")
    return <FormBuilderHeader />;

  if (route.toLocaleLowerCase() == "report-builder") {
    return <EditReport title="Edit Report" />;
  }

  if (route.toLocaleLowerCase() == "download-report") {
    return <DownloadReportHeader title="Custom Report" />;
  }

  if (route.toLocaleLowerCase() == "lead-engine") {
    return <LeadEngineHeader />;
  }
  if (route.toLocaleLowerCase() === "reports") {
    return <ReportsHeader title="Reports" />;
  }
  if (route.toLocaleLowerCase() === "integration") {
    return <IntegrationHeader />;
  }
  if (route.toLocaleLowerCase() === "users") {
    return <UsersHeader />;
  }
  const modalName = data.modal.modalName;
  const isModalActive =
    modalName === "mailchimp-api-key" || modalName === "dashboard-welcome";
  // Editing dashboards is a premium feature, gated by the ACTIVE workspace's
  // owner plan (§WS1) — a Basic member of a Premium/Standard workspace can edit.
  // `planType` is the owner's status-adjusted effective plan, so an inactive
  // sub already reads as BASIC. A frozen workspace also disables edits.
  const canEditContent = can("content:write");
  const isBasicWorkspace = isBasicPlan(planType);
  const shouldDisableEdit = isBasicWorkspace || isFrozen;

  return (
    <div
      className={cn(
        "flex h-[80px] items-center justify-between bg-white px-5 xl:h-[120px] 2xl:px-10",
        isModalActive ? "bg-black/[20%] pointer-events-none opacity-50" : "",
        className,
      )}
      style={isModalActive ? { filter: "grayscale(100%)" } : {}}
    >
      <div className="flex">
        <Text className="font-semibold capitalize text-kd-lte-blue">
          {route == "home" ? "Dashboard" : route}
        </Text>
      </div>

      <div className="flex min-w-0 flex-nowrap items-center justify-center space-x-2">
        {route.toLocaleLowerCase() !== "settings" && (
          <div className="ml-10 flex min-w-0 flex-nowrap items-center space-x-2.5 xl:space-x-[20px]">
            <div className="min-w-0">
              <DashboardDropdown invertColors={false} disabled={disabled} />
            </div>

            {canEditContent &&
              (shouldDisableEdit ? (
                <div className="group relative shrink-0">
                  <EditButton
                    href="/dashboard/edit"
                    disabled={disabled || shouldDisableEdit}
                  />

                  <PremiumUpgradeButton className="absolute bottom-full hidden group-hover:flex " />
                </div>
              ) : (
                <EditButton
                  href="/dashboard/edit"
                  disabled={disabled}
                  className="shrink-0"
                />
              ))}
          </div>
        )}

        {/* <ThemeToggle /> — hidden for now (not in the current design) */}

        <UserProfileSidebar
          userName={
            data?.userDetails?.userName || session?.user?.name || "User"
          }
          userRole={data?.userDetails?.role || "user"}
          userEmail={data?.userDetails?.email || session?.user?.email || ""}
          profilePicture={profilePicture}
          userImage={session?.user?.image}
          isAdmin={isAdminUser}
        />
      </div>
    </div>
  );
};

export default Header;

const DownloadReportHeader = ({ title }: { title: string }) => {
  // Currently returns empty fragment - title parameter reserved for future implementation
  console.log(title); // Temporary to avoid ESLint unused variable warning
  return <></>;
};

const EditDashboardHeader = ({
  title = "Edit Dashboard",
}: {
  title?: string;
}) => {
  const { data } = useZStore();
  const disabled = data.isBannerVisible ?? true;

  return (
    <div className=" flex  h-[80px] w-full items-center justify-between bg-kd-lte-blue px-[23px] xl:h-[120px] xl:px-10">
      <Text className="capitalize text-white">{title}</Text>
      <div className="">
        <DashboardDropdown
          showCreateOption={true}
          invertColors={true}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const EditReport = ({ title = "Edit Report" }: { title?: string }) => {
  const { data } = useZStore();
  return (
    // <div className=" flex h-20 w-full items-center justify-between bg-kd-lte-blue px-[23px] md:h-[60px] xl:h-[120px] xl:px-10">
    <div
      className={cn(
        "flex w-full items-center justify-between bg-kd-lte-blue px-[23px] h-[80px] xl:px-10 xl:h-[120px]",
      )}
    >
      <Text className="capitalize text-white">{title}</Text>
      {!data.modal.isOpen && (
        <CustomReportDropdown
          invertColors={true}
          truncateLength={55}
          isEditPage={true}
        />
      )}
    </div>
  );
};

const FormBuilderHeader = ({
  title = "Edit Audit Form",
}: {
  title?: string;
}) => {
  const { data } = useZStore();
  const disabled = data.isBannerVisible ?? true;

  return (
    <div className=" flex h-[80px] w-full items-center justify-between bg-kd-lte-blue px-[23px] xl:h-[120px] xl:px-10">
      {/* <Text className="text-[18px] capitalize text-white xl:text-[24px] 2xl:text-[38px]"> */}
      <TextComponent type="h2" className="text-white">
        {title}
      </TextComponent>
      {/* </Text> */}
      <FormSelectorDropdown
        disabled={disabled}
        invertColors={true}
        isEditPage={true}
      />
    </div>
  );
};

const Text = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <TextComponent type="h2" className={cn("text-kd-lte-blue", className)}>
      {children}
    </TextComponent>
    // <h1
    //   className={cn(
    //     `text-left text-[18px] font-semibold leading-[25.2px] md:text-[18px] md:leading-[25.2px] xl:text-[24px] xl:leading-[33.6px] 2xl:text-[36px] 2xl:leading-[50.4px]`,
    //     className,
    //   )}
    // >
    //   {children}
    // </h1>
  );
};

const ReportsHeader = ({ title = "Reports" }: { title?: string }) => {
  const { data } = useZStore();
  const { data: session } = useSession();
  const isAdminUser = Boolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (session?.user as any)?.isAdmin ||
      session?.user?.email === "yashkad412@gmail.com",
  );
  const disabled = data.isBannerVisible ?? true;
  // Editing reports is a premium feature scoped to the ACTIVE workspace (§WS1)
  // and requires content:write (§WS3). Exporting stays available to everyone
  // (data:export is a guest capability). @see the dashboard header above.
  const { can, planType, isFrozen } = useWorkspacePermissions();
  const canEditContent = can("content:write");
  const isBasicWorkspace = isBasicPlan(planType);
  const shouldDisableEdit = isBasicWorkspace || isFrozen;
  return (
    <div className=" flex h-20 w-full items-center justify-between bg-white px-[23px] shadow-sm md:h-[80px] xl:h-[120px]">
      <div className="flex space-x-2">
        <Text className="capitalize text-kd-lte-blue">{title}</Text>
      </div>
      <div className="flex items-center space-x-2">
        <div className="flex space-x-[9px] xl:space-x-5">
          <Button
            variant="secondary"
            disabled={disabled}
            className={cn(
              "h-[36px] w-fit space-x-1 rounded-[8px] border bg-kd-secondary-7 px-2 text-base text-kd-lte-blue hover:border-kd-lte-blue xl:h-[60px] xl:space-x-2 xl:rounded-2xl",
              disabled
                ? "hover:bg-kd-secondary-7 border-none hover:border-none bg-kd-secondary-7 cursor-not-allowed"
                : "",
            )}
            onClick={
              disabled
                ? undefined
                : () => {
                    const baseUrl = production
                      ? "https://engine321.com/"
                      : window.location.origin; // Gets the base URL of your site

                    window.open(
                      `${baseUrl}/view-report/ur-${data.selectedCustomReport}?uid=${data.userId || session?.user?.id}`,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }
            }
          >
            <ExportReportIcon
              className={cn(
                "text-kd-lte-blue size-[23px]",
                disabled ? "text-kd-secondary-4" : "",
              )}
            />
            <TextComponent
              className={cn(
                "hidden text-kd-secondary-1 md:flex",
                disabled ? "text-kd-secondary-4" : "",
              )}
              type="h4"
            >
              Export Report
            </TextComponent>
          </Button>

          <CustomReportDropdown
            disabled={disabled}
            invertColors={false}
            truncateLength={55}
          />
          {canEditContent &&
            (shouldDisableEdit ? (
              <div className="group relative">
                <EditButton
                  href="/dashboard/report-builder"
                  disabled={disabled || shouldDisableEdit}
                />

                <PremiumUpgradeButton className="absolute bottom-full hidden group-hover:flex " />
              </div>
            ) : (
              <EditButton
                href="/dashboard/report-builder"
                disabled={disabled}
              />
            ))}

          {/* <ShareDropdown /> */}
        </div>
        {/* <ThemeToggle /> — hidden for now (not in the current design) */}
        <UserProfileSidebar
          userName={
            data?.userDetails?.userName || session?.user?.name || "User"
          }
          userRole={data?.userDetails?.role || "user"}
          userEmail={data?.userDetails?.email || session?.user?.email || ""}
          profilePicture={data?.profilePicture || session?.user?.image}
          userImage={session?.user?.image}
          isAdmin={isAdminUser}
        />
      </div>
    </div>
  );
};

const IntegrationHeader = () => {
  const { data } = useZStore();
  const { data: session } = useSession();
  const router = useRouter();
  const isAdminUser = Boolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (session?.user as any)?.isAdmin ||
      session?.user?.email === "yashkad412@gmail.com",
  );
  // Only roles that can manage integrations see the add action (§WS3); the
  // server also 403s, this just avoids a dead button. Hidden on a frozen
  // workspace, where all mutations are paused.
  const { can, isFrozen } = useWorkspacePermissions();
  const canAddIntegration = can("integration:manage") && !isFrozen;

  return (
    <div className="flex h-[80px] w-full items-center justify-between bg-white px-5 xl:h-[120px] 2xl:px-10">
      <Text className="capitalize text-kd-lte-blue">Integration</Text>
      <div className="flex items-center gap-4">
        {canAddIntegration && (
          <button
            type="button"
            onClick={() => router.push("/dashboard/integration?openModal=true")}
            className="flex h-[40px] items-center justify-center rounded-[12px] border border-kd-secondary-6 bg-white px-4 text-[12px] font-semibold text-kd-secondary-1 transition-colors hover:border-kd-lte-blue hover:text-kd-lte-blue xl:h-[45px] xl:px-5 xl:text-[16px]"
          >
            Add Integration API
          </button>
        )}
        <UserProfileSidebar
          userName={
            data?.userDetails?.userName || session?.user?.name || "User"
          }
          userRole={data?.userDetails?.role || "user"}
          userEmail={data?.userDetails?.email || session?.user?.email || ""}
          profilePicture={data?.profilePicture || session?.user?.image}
          userImage={session?.user?.image}
          isAdmin={isAdminUser}
        />
      </div>
    </div>
  );
};

const UsersHeader = () => {
  const { data } = useZStore();
  const { data: session } = useSession();
  const router = useRouter();
  const isAdminUser = Boolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (session?.user as any)?.isAdmin ||
      session?.user?.email === "yashkad412@gmail.com",
  );
  // Only roles that can invite see the add action (§WS3); the server also 403s,
  // this just avoids a dead button. Hidden on a frozen workspace.
  const { can, isFrozen } = useWorkspacePermissions();
  const canInvite = can("member:invite") && !isFrozen;

  return (
    <div className="flex h-[80px] w-full items-center justify-between bg-white px-5 xl:h-[120px] 2xl:px-10">
      <Text className="capitalize text-kd-lte-blue">Users</Text>
      <div className="flex items-center gap-4">
        {canInvite && (
          <button
            type="button"
            onClick={() => router.push("/dashboard/users?openModal=true")}
            className="flex h-[40px] items-center justify-center gap-2 rounded-[12px] border border-kd-secondary-6 bg-white px-4 text-[12px] font-semibold text-kd-secondary-1 transition-colors hover:border-kd-lte-blue hover:text-kd-lte-blue xl:h-[45px] xl:px-5 xl:text-[16px]"
          >
            <FiUserPlus className="size-[18px]" />
            Add Users
          </button>
        )}
        <UserProfileSidebar
          userName={
            data?.userDetails?.userName || session?.user?.name || "User"
          }
          userRole={data?.userDetails?.role || "user"}
          userEmail={data?.userDetails?.email || session?.user?.email || ""}
          profilePicture={data?.profilePicture || session?.user?.image}
          userImage={session?.user?.image}
          isAdmin={isAdminUser}
        />
      </div>
    </div>
  );
};

const LeadEngineHeader = ({ title = "Lead Engine" }: { title?: string }) => {
  const { data } = useZStore();
  const { data: session } = useSession();
  const isAdminUser = Boolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (session?.user as any)?.isAdmin ||
      session?.user?.email === "yashkad412@gmail.com",
  );
  const disabled = data.isBannerVisible ?? true;
  // Premium follows the ACTIVE workspace's owner (§WS1), so a Basic member of a
  // Premium workspace gets the white-label form features while in it.
  const { isPremium } = useWorkspacePermissions();
  const isNonPremium = !isPremium;

  return (
    <div className=" flex h-20 w-full items-center justify-between bg-white px-[23px] shadow-sm md:h-[80px] xl:h-[120px]">
      <div className="flex space-x-2">
        <Text className="capitalize text-kd-lte-blue">{title}</Text>
      </div>
      <div className="flex items-center space-x-2">
        <div className="flex space-x-[9px] xl:space-x-5">
          {isNonPremium && (
            <div className="group relative">
              <FormSelectorDropdown disabled={disabled || isNonPremium} />
              <PremiumUpgradeButton className="absolute bottom-full hidden group-hover:flex " />
            </div>
          )}
          {!isNonPremium && <FormSelectorDropdown disabled={disabled} />}
          {isNonPremium && (
            <div className="group relative">
              <EditButton
                href={`/dashboard/form-builder/${data.selectedFormId}`}
                disabled={disabled || isNonPremium}
              />
              <PremiumUpgradeButton className="absolute bottom-full hidden group-hover:flex " />
            </div>
          )}
          {!isNonPremium && (
            <EditButton
              href={`/dashboard/form-builder/${data.selectedFormId}`}
              disabled={disabled}
            />
          )}
        </div>
        {/* <ThemeToggle /> — hidden for now (not in the current design) */}
        <UserProfileSidebar
          userName={
            data?.userDetails?.userName || session?.user?.name || "User"
          }
          userRole={data?.userDetails?.role || "user"}
          userEmail={data?.userDetails?.email || session?.user?.email || ""}
          profilePicture={data?.profilePicture || session?.user?.image}
          userImage={session?.user?.image}
          isAdmin={isAdminUser}
        />
      </div>
    </div>
  );
};
